import { useState, useEffect, useMemo } from "react";
import { useRangeSelection } from "@/hooks/use-range-selection";
import { useTabQueryParam } from "@/hooks/use-tab-query-param";
import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { useLocation, useRoute, Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/app-layout";
import { ViewOnlyBanner, usePermissions } from "@/hooks/use-permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Send, Star, Copy, Trash2, FileText, DollarSign, Plus, Upload, X, Users, Crown, Lock, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Vendor, Property, VendorDocument, VendorRateOverride, VendorReview } from "@shared/schema";
import { VENDOR_ROLES, MAINTENANCE_SPECIALTIES } from "@shared/schema";
import AddressAutocomplete from "@/components/address-autocomplete";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";

const ROLE_LABELS: Record<string, string> = {
  cleaning: "Cleaning",
  maintenance: "Maintenance",
  property_manager: "Property Manager",
  other: "Other",
};

const SPECIALTY_LABELS: Record<string, string> = {
  electrical: "Electrical",
  hvac: "HVAC",
  handyman: "Handyman",
  plumbing: "Plumbing",
  landscaping: "Landscaping",
  pest_control: "Pest Control",
  roof_repair: "Roof Repair",
  laundry_repair: "Laundry Repair",
  painting: "Painting",
  appliance_repair: "Appliance Repair",
  locksmith: "Locksmith",
  flooring: "Flooring",
  drywall: "Drywall",
  carpentry: "Carpentry",
  window_repair: "Window Repair",
  garage_door: "Garage Door",
};

export default function AppOperationsVendorDetail() {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [, params] = useRoute("/app/operations/vendors/:id");
  const vendorId = params?.id ? parseInt(params.id, 10) : 0;

  const vendorQuery = useQuery<Vendor & { isLinked?: boolean; relationshipId?: number | null }>({
    queryKey: ["/api/app/operations/vendors", vendorId],
    enabled: !!vendorId,
  });
  // Roster-linked vendor (owned by another tenant): only the per-host overlay
  // (roles / specialties / properties / priority) is editable, via the
  // relationship endpoint. Contact/ACH/marketplace fields belong to the owner.
  const isLinked = vendorQuery.data?.isLinked === true;
  const OVERLAY_FIELDS = ["roles", "serviceTypes", "propertyIds", "priority"];
  usePageTitle(vendorQuery.data?.name || "Vendor");

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
  });

  const [company, setCompany] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [priority, setPriority] = useState<string>("");
  const [marketplaceBlocked, setMarketplaceBlocked] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([]);
  const [propertySearch, setPropertySearch] = useState("");
  const [initialized, setInitialized] = useState(false);
  // Task #1907 — Mercury ACH payout fields. Only rendered when the host has
  // the `mercury_vendor_pay` beta enabled.
  const [achRouting, setAchRouting] = useState("");
  const [achAccount, setAchAccount] = useState("");
  const [achAccountType, setAchAccountType] = useState("checking");

  const betaAccessQuery = useQuery<string[]>({
    queryKey: ["/api/app/beta/access"],
  });
  const hasMercuryBeta = (betaAccessQuery.data || []).includes("mercury_vendor_pay");

  useEffect(() => {
    if (vendorQuery.data && !initialized) {
      const v = vendorQuery.data;
      setCompany(v.businessName || "");
      setFirstName(v.firstName || "");
      setLastName(v.lastName || "");
      setEmail(v.email || "");
      setPhone(v.phone || "");
      setAddress(v.address || "");
      setPriority(v.priority != null ? String(v.priority) : "");
      setMarketplaceBlocked(v.isMarketplaceBlocked === true);
      setSelectedRoles(Array.isArray(v.roles) ? v.roles as string[] : []);
      setSelectedSpecialties(Array.isArray(v.serviceTypes) ? v.serviceTypes as string[] : []);
      setSelectedPropertyIds(Array.isArray(v.propertyIds) ? v.propertyIds as number[] : []);
      setAchRouting((v as any).achRoutingNumber || "");
      setAchAccount((v as any).achAccountNumber || "");
      setAchAccountType((v as any).achAccountType || "checking");
      setInitialized(true);
    }
  }, [vendorQuery.data, initialized]);

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<any>) => {
      const res = isLinked
        ? await apiRequest("PATCH", `/api/app/operations/marketplace/${vendorId}/relationship`, data)
        : await apiRequest("PATCH", `/api/app/operations/vendors/${vendorId}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendorId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/app/operations/vendors/${vendorId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors"] });
      toast({ title: isLinked ? "Vendor removed from your roster" : "Vendor deleted" });
      navigate("/app/operations/vendors");
    },
  });

  const inviteMutation = useMutation({
    mutationFn: async (method: string) => {
      const res = await apiRequest("POST", `/api/app/operations/vendors/${vendorId}/invite`, { method });
      return res.json();
    },
    onSuccess: (data: any) => {
      toast({ title: data.message });
    },
    onError: (e: any) => {
      toast({ title: "Error", description: parseErrorMessage(e, "Couldn't update this vendor. Please try again."), variant: "destructive" });
    },
  });

  const saveField = (field: string, value: any) => {
    if (readOnly) return;
    if (isLinked && !OVERLAY_FIELDS.includes(field)) {
      toast({
        title: "Managed by the vendor's owner",
        description: "For a linked vendor you can only change roles, specialties, properties, and order.",
      });
      return;
    }
    updateMutation.mutate({ [field]: value });
  };

  const toggleRole = (role: string) => {
    if (readOnly) return;
    const updated = selectedRoles.includes(role)
      ? selectedRoles.filter((r) => r !== role)
      : [...selectedRoles, role];
    setSelectedRoles(updated);
    updateMutation.mutate({ roles: updated });
  };

  const toggleSpecialty = (specialty: string) => {
    if (readOnly) return;
    const updated = selectedSpecialties.includes(specialty)
      ? selectedSpecialties.filter((s) => s !== specialty)
      : [...selectedSpecialties, specialty];
    setSelectedSpecialties(updated);
    updateMutation.mutate({ serviceTypes: updated });
  };

  const toggleProperty = (propertyId: number) => {
    if (readOnly) return;
    const updated = selectedPropertyIds.includes(propertyId)
      ? selectedPropertyIds.filter((id) => id !== propertyId)
      : [...selectedPropertyIds, propertyId];
    setSelectedPropertyIds(updated);
    updateMutation.mutate({ propertyIds: updated });
  };
  const VENDOR_LIVE_STATUSES = ["active", "launch", "renovations"];
  const allPropertiesForHook = useMemo(() => {
    const props = (propertiesQuery.data || []).filter((p) =>
      VENDOR_LIVE_STATUSES.includes(p.status),
    );
    return propertySearch
      ? props.filter((p) =>
          String(p.name ?? "").toLowerCase().includes(propertySearch.toLowerCase()),
        )
      : props;
  }, [propertiesQuery.data, propertySearch]);
  const selectedPropertySet = useMemo(() => new Set(selectedPropertyIds), [selectedPropertyIds]);
  const { handleItemClick: handlePropertyClick } = useRangeSelection(
    allPropertiesForHook.map((p) => p.id),
    selectedPropertySet,
    (next) => {
      if (readOnly) return;
      const arr = Array.from(next);
      setSelectedPropertyIds(arr);
      updateMutation.mutate({ propertyIds: arr });
    },
  );

  if (vendorQuery.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 max-w-3xl mx-auto">
          <Skeleton className="h-8 w-48 mb-6" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  const vendor = vendorQuery.data;
  if (!vendor) {
    return (
      <AppLayout>
        <div className="p-6 max-w-3xl mx-auto">
          <p className="text-gray-500">Vendor not found.</p>
        </div>
      </AppLayout>
    );
  }

  const LIVE_STATUSES = ["active", "launch", "renovations"];
  const properties = (propertiesQuery.data || []).filter((p) => LIVE_STATUSES.includes(p.status));
  const livePropertyIdSet = new Set(properties.map((p) => p.id));
  const hiddenSelectedIds = selectedPropertyIds.filter((id) => !livePropertyIdSet.has(id));
  const filteredProperties = propertySearch
    ? properties.filter((p) => String(p.name ?? "").toLowerCase().includes(propertySearch.toLowerCase()))
    : properties;

  return (
    <AppLayout>
      <div className="p-6 max-w-3xl mx-auto" data-testid="page-vendor-detail">
        <ViewOnlyBanner area="maintenance" />
        {isLinked && (
          <div className="mb-4 rounded-lg border border-brand/40 bg-brand-light px-3 py-2 text-sm text-gray-700" data-testid="banner-linked-vendor">
            This vendor is on your roster via a link — their contact and account details are managed by the
            vendor's owner. You can set the roles, specialties, properties, and order they have for your account.
          </div>
        )}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <Link
              href="/app/operations/vendors"
              className="text-brand hover:text-brand-hover flex items-center gap-1 text-sm"
              data-testid="button-back-vendors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Link>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              className="gap-1"
              onClick={() => inviteMutation.mutate("sms")}
              disabled={readOnly || (!vendor.phone)}
              data-testid="button-invite-vendor"
            >
              <Send className="h-4 w-4" />
              Invite {vendor.firstName || String(vendor.name ?? "").split(" ")[0]} to Vendor Portal
            </Button>
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="vendor-detail" />
              <GuideButton pageKey="vendor-detail" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="vendor-detail" />
        </div>

        <div className="flex items-center gap-4 mb-8">
          <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center text-lg font-semibold text-gray-600">
            {vendor.firstName && vendor.lastName
              ? `${vendor.firstName[0]}${vendor.lastName[0]}`
              : String(vendor.name ?? "").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="text-vendor-detail-name" data-tour="vendor-detail-heading">{vendor.name}</h1>
          {(vendor as any).marketplaceTier === "pro" && (
            <span className="inline-flex items-center gap-1 rounded-full bg-brand-accent px-2 py-0.5 text-xs font-semibold text-white" data-testid={`badge-pro-detail-${vendor.id}`}>
              <Crown className="h-3 w-3" />
              Pro
            </span>
          )}
        </div>

        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Star className="h-4 w-4 text-gray-400" />
            <h2 className="text-base font-semibold text-gray-900">Details</h2>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Company</Label>
              <Input
                disabled={readOnly}
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                onBlur={() => saveField("businessName", company || null)}
                data-testid="input-vendor-detail-company"
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">First name</Label>
              <Input
                disabled={readOnly}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onBlur={() => {
                  saveField("firstName", firstName || null);
                  if (firstName && lastName) {
                    saveField("name", `${firstName} ${lastName}`);
                  }
                }}
                data-testid="input-vendor-detail-first-name"
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Last name</Label>
              <Input
                disabled={readOnly}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onBlur={() => {
                  saveField("lastName", lastName || null);
                  if (firstName && lastName) {
                    saveField("name", `${firstName} ${lastName}`);
                  }
                }}
                data-testid="input-vendor-detail-last-name"
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Email</Label>
              <Input
                disabled={readOnly}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => saveField("email", email || null)}
                data-testid="input-vendor-detail-email"
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Phone</Label>
              {(vendorQuery.data as any)?.phoneClaimedByVendor ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="relative">
                        <Input
                          disabled
                          type="tel"
                          value={phone}
                          placeholder="e.g. +12015551234"
                          data-testid="input-vendor-detail-phone"
                          className="pr-8"
                        />
                        <Lock className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      Vendor has claimed this number. Only the vendor can change it now.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <Input
                  disabled={readOnly}
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  onBlur={() => saveField("phone", phone || null)}
                  placeholder="e.g. +12015551234"
                  data-testid="input-vendor-detail-phone"
                />
              )}
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Address</Label>
              <AddressAutocomplete
                disabled={readOnly}
                value={address}
                onSelect={(addr) => {
                  setAddress(addr);
                  saveField("address", addr || null);
                }}
                onChange={(addr) => setAddress(addr)}
                onBlurChange={(addr) => saveField("address", addr || null)}
                placeholder="Enter a location"
                data-testid="input-vendor-detail-address"
              />
            </div>
            <div className="grid grid-cols-[140px_1fr] items-center gap-2">
              <Label className="text-sm text-gray-600">Priority</Label>
              <div className="flex items-center gap-2">
                <Input
                  disabled={readOnly}
                  type="number"
                  min={1}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  onBlur={() => {
                    const num = parseInt(priority, 10);
                    if (priority === "") {
                      saveField("priority", null);
                    } else if (!isNaN(num) && num > 0) {
                      saveField("priority", num);
                    } else {
                      setPriority(vendorQuery.data?.priority != null ? String(vendorQuery.data.priority) : "");
                    }
                  }}
                  placeholder="e.g. 1"
                  className="w-32"
                  data-testid="input-vendor-detail-priority"
                />
                <span className="text-xs text-gray-400">Lower number = higher priority</span>
              </div>
            </div>
            <div className="grid grid-cols-[140px_1fr] items-start gap-2">
              <Label className="text-sm text-gray-600">Marketplace</Label>
              <div className="flex items-start gap-2">
                <Checkbox
                  id="vendor-detail-marketplace-blocked"
                  disabled={readOnly}
                  checked={marketplaceBlocked}
                  onCheckedChange={(checked) => {
                    const next = checked === true;
                    setMarketplaceBlocked(next);
                    saveField("isMarketplaceBlocked", next);
                  }}
                  className="mt-0.5"
                  data-testid="checkbox-vendor-detail-marketplace-blocked"
                />
                <Label
                  htmlFor="vendor-detail-marketplace-blocked"
                  className="flex items-center gap-1.5 text-sm font-normal leading-snug cursor-pointer"
                >
                  Block vendor from being shown in the marketplace
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-3.5 w-3.5 text-gray-400 shrink-0" data-testid="tooltip-vendor-detail-marketplace-blocked" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">
                        Vendor will only be able to be added by someone directly who already has their phone number.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </Label>
              </div>
            </div>
          </div>
        </section>

        {hasMercuryBeta && (
          <section className="mb-8" data-testid="section-vendor-ach">
            <div className="flex items-center gap-2 mb-4">
              <DollarSign className="h-4 w-4 text-gray-400" />
              <h2 className="text-base font-semibold text-gray-900">Mercury ACH payout</h2>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Add the vendor's bank routing &amp; account so you can pay them via Mercury ACH instead of a card charge.
              These fields are only visible to you; PadPulse uses them to create a Mercury recipient on first payout.
            </p>
            <div className="space-y-4">
              <div className="grid grid-cols-[140px_1fr] items-center gap-2">
                <Label className="text-sm text-gray-600">Routing #</Label>
                <Input
                  disabled={readOnly}
                  value={achRouting}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  onChange={(e) => setAchRouting(e.target.value.replace(/\D/g, ""))}
                  onBlur={() => saveField("achRoutingNumber", achRouting || null)}
                  placeholder="9 digits"
                  data-testid="input-vendor-ach-routing"
                />
              </div>
              <div className="grid grid-cols-[140px_1fr] items-center gap-2">
                <Label className="text-sm text-gray-600">Account #</Label>
                <Input
                  disabled={readOnly}
                  value={achAccount}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={17}
                  onChange={(e) => setAchAccount(e.target.value.replace(/\D/g, ""))}
                  onBlur={() => saveField("achAccountNumber", achAccount || null)}
                  data-testid="input-vendor-ach-account"
                />
              </div>
              <div className="grid grid-cols-[140px_1fr] items-center gap-2">
                <Label className="text-sm text-gray-600">Account type</Label>
                <Select
                  value={achAccountType}
                  onValueChange={(val) => {
                    setAchAccountType(val);
                    saveField("achAccountType", val);
                  }}
                  disabled={readOnly}
                >
                  <SelectTrigger className="w-48" data-testid="select-vendor-ach-account-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="checking">Checking</SelectItem>
                    <SelectItem value="savings">Savings</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(vendorQuery.data as any)?.mercuryRecipientId && (
                <p className="text-xs text-gray-400" data-testid="text-vendor-mercury-recipient">
                  Linked Mercury recipient: {(vendorQuery.data as any).mercuryRecipientId}
                </p>
              )}
            </div>
          </section>
        )}

        <Separator className="my-6" />

        <section className="mb-8">
          <div className="border rounded-lg p-4" data-tour="vendor-detail-info">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Roles</h3>
              <span className="text-sm text-gray-500">{selectedRoles.length} selected</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {VENDOR_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 cursor-pointer" data-testid={`checkbox-role-${role}`}>
                  <Checkbox
                    disabled={readOnly}
                    checked={selectedRoles.includes(role)}
                    onCheckedChange={() => toggleRole(role)}
                  />
                  <span className="text-sm text-gray-700">{ROLE_LABELS[role] || role}</span>
                </label>
              ))}
            </div>
          </div>

          {selectedRoles.includes("maintenance") && (
            <div className="border rounded-lg p-4 mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Maintenance Specialties</h3>
                <span className="text-sm text-gray-500">{selectedSpecialties.length} selected</span>
              </div>
              <p className="text-xs text-gray-500 mb-3">Select the types of maintenance this vendor handles</p>
              <div className="grid grid-cols-2 gap-3">
                {MAINTENANCE_SPECIALTIES.map((spec) => (
                  <label key={spec} className="flex items-center gap-2 cursor-pointer" data-testid={`checkbox-specialty-${spec}`}>
                    <Checkbox
                      disabled={readOnly}
                      checked={selectedSpecialties.includes(spec)}
                      onCheckedChange={() => toggleSpecialty(spec)}
                    />
                    <span className="text-sm text-gray-700">{SPECIALTY_LABELS[spec] || spec}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </section>

        <section className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-sm text-gray-400">⊙</span>
            <h3 className="font-semibold text-gray-900">Preferred properties</h3>
            <span className="text-xs text-gray-500">Does this vendor work at specific properties?</span>
          </div>
          <div className="border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Checkbox
                  disabled={readOnly}
                  checked={properties.length > 0 && properties.every((p) => selectedPropertyIds.includes(p.id))}
                  onCheckedChange={() => {
                    if (readOnly) return;
                    const allLiveSelected = properties.every((p) => selectedPropertyIds.includes(p.id));
                    if (allLiveSelected) {
                      setSelectedPropertyIds(hiddenSelectedIds);
                      updateMutation.mutate({ propertyIds: hiddenSelectedIds });
                    } else {
                      const allIds = [...hiddenSelectedIds, ...properties.map((p) => p.id)];
                      setSelectedPropertyIds(allIds);
                      updateMutation.mutate({ propertyIds: allIds });
                    }
                  }}
                />
                <span className="font-medium text-sm">Properties</span>
              </div>
              <span className="text-sm text-gray-500">{selectedPropertyIds.filter((id) => livePropertyIdSet.has(id)).length} selected</span>
            </div>
            <Input
              placeholder="Search..."
              value={propertySearch}
              onChange={(e) => setPropertySearch(e.target.value)}
              className="mb-3"
              data-testid="input-property-search"
            />
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {filteredProperties.map((property) => (
                <div
                  key={property.id}
                  className="flex items-center gap-2 cursor-pointer py-1"
                  data-testid={`checkbox-property-${property.id}`}
                  onClick={(e) => { e.stopPropagation(); handlePropertyClick(property.id, e.shiftKey); }}
                >
                  <Checkbox
                    disabled={readOnly}
                    checked={selectedPropertyIds.includes(property.id)}
                    onCheckedChange={() => {}}
                  />
                  <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                    {property.name[0]}
                  </div>
                  <span className="text-sm text-gray-700">{property.name}</span>
                </div>
              ))}
              {filteredProperties.length === 0 && (
                <p className="text-sm text-gray-400 py-2">No properties found</p>
              )}
            </div>
          </div>
        </section>

        {!isLinked && (
          <>
            <Separator className="my-6" />
            <VendorExtras vendorId={vendor.id} vendor={vendor} />
          </>
        )}

        <Separator className="my-6" />

        <div className="flex items-center gap-3">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              const prompt = isLinked
                ? "Remove this vendor from your roster? Their account is unaffected."
                : "Are you sure you want to delete this vendor?";
              if (confirm(prompt)) {
                deleteMutation.mutate();
              }
            }}
            disabled={readOnly || (deleteMutation.isPending)}
            data-testid="button-delete-vendor"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            {isLinked ? "Remove from roster" : "Delete"}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(String(vendor.id));
              toast({ title: "ID copied to clipboard" });
            }}
            data-testid="button-copy-id"
          >
            <Copy className="h-4 w-4 mr-1" />
            Copy ID
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}

const DOC_TYPES = ["w9", "insurance", "license", "contract", "other"];
const DOC_LABELS: Record<string, string> = { w9: "W-9", insurance: "Insurance", license: "License", contract: "Contract", other: "Other" };
const TASK_TYPES = ["cleaning", "maintenance", "plumbing", "electrical", "hvac", "general"];

interface VendorInvoiceRow {
  id: number;
  fileName: string;
  uploadedAt: string;
  invoiceNumber: string | null;
  totalAmount: number;
  primaryPropertyId: number;
  propertyIds: number[];
  expenses?: Array<{ id: number; propertyId: string; amount: string; description: string; date: string }>;
}

type GroupedInvoicesResponse = { groups: Array<{ propertyId: number; propertyName: string; total: number; invoices: VendorInvoiceRow[] }>; grandTotal: number };

function VendorInvoices({ vendorId }: { vendorId: number }) {
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const [splitByProperty, setSplitByProperty] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const invoicesQuery = useQuery<VendorInvoiceRow[] | GroupedInvoicesResponse>({
    queryKey: ["/api/app/operations/vendors", vendorId, "invoices", splitByProperty],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors/${vendorId}/invoices?splitByProperty=${splitByProperty}`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load invoices");
      return res.json();
    },
  });
  const propertiesQuery = useQuery<Array<{ id: number; name: string }>>({ queryKey: ["/api/app/properties"] });
  const propMap = new Map((propertiesQuery.data || []).map(p => [p.id, p.name] as const));

  const isGrouped = splitByProperty && invoicesQuery.data && !Array.isArray(invoicesQuery.data);
  const flatInvoices: VendorInvoiceRow[] = !isGrouped ? ((invoicesQuery.data as VendorInvoiceRow[]) || []) : [];
  const groupedData = isGrouped ? (invoicesQuery.data as GroupedInvoicesResponse) : null;
  const total = isGrouped ? (groupedData?.grandTotal || 0) : flatInvoices.reduce((s, inv) => s + inv.totalAmount, 0);
  const invoiceCount = isGrouped ? (groupedData?.groups.reduce((s, g) => s + g.invoices.length, 0) || 0) : flatInvoices.length;

  const renderInvoiceRow = (inv: VendorInvoiceRow) => {
    const isOpen = expanded.has(inv.id);
    return (
      <div key={inv.id} className="border rounded-lg" data-testid={`invoice-${inv.id}`}>
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="h-5 w-5 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <a href={`/api/app/property-documents/${inv.id}/file`} target="_blank" rel="noreferrer" className="text-sm font-medium text-blue-600 hover:underline truncate block" data-testid={`link-invoice-${inv.id}`}>{inv.fileName}</a>
              <p className="text-xs text-gray-400">
                {inv.invoiceNumber ? `#${inv.invoiceNumber} · ` : ""}
                {new Date(inv.uploadedAt).toLocaleDateString()}
                {inv.propertyIds.length > 0 && ` · ${inv.propertyIds.length} ${inv.propertyIds.length === 1 ? "property" : "properties"}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-gray-900">${inv.totalAmount.toFixed(2)}</span>
            {inv.expenses && inv.expenses.length > 0 && (
              <Button disabled={readOnly} variant="ghost" size="sm" onClick={() => {
                const next = new Set(expanded);
                if (isOpen) next.delete(inv.id); else next.add(inv.id);
                setExpanded(next);
              }} data-testid={`button-expand-invoice-${inv.id}`}>{isOpen ? "Hide" : "Details"}</Button>
            )}
          </div>
        </div>
        {isOpen && inv.expenses && (
          <div className="border-t bg-gray-50 px-3 py-2 space-y-1">
            {inv.expenses.map(e => (
              <div key={e.id} className="flex items-center justify-between text-xs" data-testid={`invoice-${inv.id}-expense-${e.id}`}>
                <div>
                  <span className="font-medium">{propMap.get(parseInt(String(e.propertyId))) || `Property ${e.propertyId}`}</span>
                  <span className="text-gray-400 ml-2">{e.description}</span>
                </div>
                <span className="font-medium">${parseFloat(e.amount).toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div data-testid="vendor-invoices-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm text-gray-500">Contractor invoices uploaded for this vendor across properties</p>
          <p className="text-xs text-gray-400 mt-1">Total: ${total.toFixed(2)} ({invoiceCount} invoices{isGrouped ? `, ${groupedData?.groups.length || 0} properties` : ""})</p>
        </div>
        <div className="flex items-center gap-2">
          <Label htmlFor="split-by-property" className="text-sm font-normal cursor-pointer">Split by property</Label>
          <Switch id="split-by-property" checked={splitByProperty} onCheckedChange={setSplitByProperty} data-testid="switch-split-by-property" />
        </div>
      </div>
      {invoicesQuery.isLoading && <p className="text-sm text-gray-400 py-4 text-center">Loading…</p>}
      {!invoicesQuery.isLoading && invoiceCount === 0 && (
        <p className="text-sm text-gray-400 py-4 text-center" data-testid="text-no-invoices">No invoices uploaded for this vendor yet.</p>
      )}
      {isGrouped && groupedData && (
        <div className="space-y-4">
          {groupedData.groups.map(g => (
            <div key={g.propertyId} data-testid={`invoice-group-${g.propertyId}`}>
              <div className="flex items-center justify-between mb-2 pb-2 border-b">
                <h4 className="text-sm font-semibold text-gray-900">{g.propertyName}</h4>
                <span className="text-sm font-semibold text-gray-700">${g.total.toFixed(2)}</span>
              </div>
              <div className="space-y-2">{g.invoices.map(renderInvoiceRow)}</div>
            </div>
          ))}
        </div>
      )}
      {!isGrouped && (
        <div className="space-y-2">
          {flatInvoices.map(renderInvoiceRow)}
        </div>
      )}
    </div>
  );
}

interface VendorTeamMember {
  id: number;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string | null;
  role: "owner" | "member";
  status: "active" | "invited" | "removed";
  acceptedAt?: string | null;
}

function VendorTeamReadOnly({ vendorId }: { vendorId: number }) {
  const { data: allMembers = [], isLoading } = useQuery<VendorTeamMember[]>({
    queryKey: ["/api/app/operations/vendors", vendorId, "team"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors/${vendorId}/team`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  // Show only active members on the host-side view
  const members = allMembers.filter(m => m.status === "active");

  if (isLoading) return <p className="text-sm text-gray-400 text-center py-4">Loading team...</p>;
  if (members.length === 0) {
    return (
      <div className="text-center py-6">
        <Users className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-400">No active team members yet.</p>
        <p className="text-xs text-gray-400 mt-1">The vendor manages their team through the vendor portal.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-gray-500 mb-3">Active team members — read only.</p>
      {members.map(m => (
        <Card key={m.id} data-testid={`card-vendor-team-${m.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              {m.role === "owner"
                ? <Crown className="h-4 w-4 text-blue-500" />
                : <span className="text-xs font-medium text-blue-500">{m.firstName[0]}{m.lastName[0]}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{m.firstName} {m.lastName}</p>
              <p className="text-xs text-gray-400">{m.phone}</p>
              {m.email && <p className="text-xs text-gray-400 truncate">{m.email}</p>}
              {m.acceptedAt && <p className="text-xs text-gray-300">Joined {new Date(m.acceptedAt).toLocaleDateString()}</p>}
            </div>
            <Badge variant={m.role === "owner" ? "default" : "secondary"} className="text-xs">
              {m.role === "owner" ? "Owner" : "Member"}
            </Badge>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function VendorExtras({ vendorId, vendor }: { vendorId: number; vendor: Vendor }) {
  const queryClient = useQueryClient();
  const { canEdit: __canEditArea } = usePermissions();
  const readOnly = !__canEditArea("maintenance");
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useTabQueryParam("tab", "documents", {
    tabs: ["documents", "invoices", "rates", "reviews", "team"],
  });
  const [docDialogOpen, setDocDialogOpen] = useState(false);
  const [docType, setDocType] = useState("w9");
  const [docFile, setDocFile] = useState("");
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateTaskType, setRateTaskType] = useState("cleaning");
  const [rateAmount, setRateAmount] = useState("");
  const [rateType, setRateType] = useState("fixed");

  const docsQuery = useQuery<VendorDocument[]>({
    queryKey: ["/api/app/operations/vendors", vendorId, "documents"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors/${vendorId}/documents`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const ratesQuery = useQuery<VendorRateOverride[]>({
    queryKey: ["/api/app/operations/vendors", vendorId, "rate-overrides"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors/${vendorId}/rate-overrides`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const reviewsQuery = useQuery<VendorReview[]>({
    queryKey: ["/api/app/operations/vendors", vendorId, "reviews"],
    queryFn: async () => {
      const res = await fetch(`/api/app/operations/vendors/${vendorId}/reviews`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
  });

  const createDocMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", `/api/app/operations/vendors/${vendorId}/documents`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendorId, "documents"] });
      toast({ title: "Document added" });
      setDocDialogOpen(false);
      setDocFile("");
    },
    onError: () => toast({ title: "Failed to add document", variant: "destructive" }),
  });

  const deleteDocMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/operations/vendors/${vendorId}/documents/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendorId, "documents"] });
      toast({ title: "Document removed" });
    },
  });

  const createRateMutation = useMutation({
    mutationFn: async (data: any) => {
      return apiRequest("POST", `/api/app/operations/vendors/${vendorId}/rate-overrides`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendorId, "rate-overrides"] });
      toast({ title: "Rate override added" });
      setRateDialogOpen(false);
      setRateAmount("");
    },
    onError: () => toast({ title: "Failed to add rate", variant: "destructive" }),
  });

  const deleteRateMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest("DELETE", `/api/app/operations/vendors/${vendorId}/rate-overrides/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/operations/vendors", vendorId, "rate-overrides"] });
      toast({ title: "Rate override removed" });
    },
  });

  const docs = docsQuery.data || [];
  const rates = ratesQuery.data || [];
  const reviews = reviewsQuery.data || [];

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDocFile(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <TabsList className="w-full">
        <TabsTrigger value="documents" className="flex-1" data-testid="tab-documents">
          <FileText className="h-3.5 w-3.5 mr-1" />
          Docs ({docs.length})
        </TabsTrigger>
        <TabsTrigger value="invoices" className="flex-1" data-testid="tab-invoices" data-tour="vendor-detail-tasks">
          <FileText className="h-3.5 w-3.5 mr-1" />
          Invoices
        </TabsTrigger>
        <TabsTrigger value="rates" className="flex-1" data-testid="tab-rates">
          <DollarSign className="h-3.5 w-3.5 mr-1" />
          Rates ({rates.length})
        </TabsTrigger>
        <TabsTrigger value="reviews" className="flex-1" data-testid="tab-reviews">
          <Star className="h-3.5 w-3.5 mr-1" />
          Reviews ({reviews.length})
        </TabsTrigger>
        <TabsTrigger value="team" className="flex-1" data-testid="tab-vendor-team">
          <Users className="h-3.5 w-3.5 mr-1" />
          Team
        </TabsTrigger>
      </TabsList>

      <TabsContent value="invoices" className="mt-4">
        <VendorInvoices vendorId={vendorId} />
      </TabsContent>

      <TabsContent value="documents" className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-gray-500">Upload W-9s, insurance certificates, licenses</p>
          <Dialog open={docDialogOpen} onOpenChange={(o) => { if (createDocMutation.isPending) return; setDocDialogOpen(o); }}>
            <DialogTrigger asChild>
              <Button disabled={readOnly} size="sm" data-testid="button-add-document" data-tour="vendor-detail-docs">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Document
              </Button>
            </DialogTrigger>
            <DialogContent
              className="max-w-sm"
              onEscapeKeyDown={(e) => { if (createDocMutation.isPending) e.preventDefault(); }}
              onInteractOutside={(e) => { if (createDocMutation.isPending) e.preventDefault(); }}
              onPointerDownOutside={(e) => { if (createDocMutation.isPending) e.preventDefault(); }}
            >
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Document Type</Label>
                  <Select value={docType} onValueChange={setDocType}>
                    <SelectTrigger data-testid="select-doc-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DOC_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{DOC_LABELS[t]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>File</Label>
                  <div className="mt-1">
                    <label className="flex items-center gap-2 px-3 py-2 border rounded-md cursor-pointer hover:bg-gray-50" data-testid="input-doc-file">
                      <Upload className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{docFile ? "File selected" : "Choose file..."}</span>
                      <input type="file" className="hidden" onChange={handleFileUpload} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
                    </label>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Stored as base64. S3 integration coming soon.</p>
                </div>
                <Button
                  className="w-full"
                  disabled={readOnly || (!docFile || createDocMutation.isPending)}
                  onClick={() => createDocMutation.mutate({ type: docType, fileUrl: docFile })}
                  data-testid="button-submit-document"
                >
                  Upload
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {docs.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No documents uploaded yet</p>}
        <div className="space-y-2">
          {docs.map(doc => (
            <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`doc-${doc.id}`}>
              <div className="flex items-center gap-3">
                <FileText className="h-5 w-5 text-blue-500" />
                <div>
                  <p className="text-sm font-medium">{DOC_LABELS[doc.type] || doc.type}</p>
                  <p className="text-xs text-gray-400">
                    Added {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString() : ""}
                    {doc.expiresAt && ` · Expires ${new Date(doc.expiresAt).toLocaleDateString()}`}
                  </p>
                </div>
              </div>
              <Button disabled={readOnly} variant="ghost" size="sm" onClick={() => deleteDocMutation.mutate(doc.id)} data-testid={`button-delete-doc-${doc.id}`}>
                <X className="h-4 w-4 text-red-400" />
              </Button>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="rates" className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm text-gray-500">
              Default rate: {vendor.defaultRateCents ? `$${(vendor.defaultRateCents / 100).toFixed(2)}` : "Not set"}
              {vendor.rateType && ` (${vendor.rateType})`}
            </p>
          </div>
          <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
            <DialogTrigger asChild>
              <Button disabled={readOnly} size="sm" data-testid="button-add-rate">
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Override
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>Add Rate Override</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                <div>
                  <Label>Task Type</Label>
                  <Select value={rateTaskType} onValueChange={setRateTaskType}>
                    <SelectTrigger data-testid="select-rate-task-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TASK_TYPES.map(t => (
                        <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Amount ($)</Label>
                  <Input
                    type="number"
                    value={rateAmount}
                    onChange={(e) => setRateAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="input-rate-amount"
                  />
                </div>
                <div>
                  <Label>Rate Type</Label>
                  <Select value={rateType} onValueChange={setRateType}>
                    <SelectTrigger data-testid="select-rate-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fixed">Fixed</SelectItem>
                      <SelectItem value="hourly">Hourly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  className="w-full"
                  disabled={readOnly || (!rateAmount || createRateMutation.isPending)}
                  onClick={() => createRateMutation.mutate({
                    taskType: rateTaskType,
                    amountCents: Math.round(Number(rateAmount) * 100),
                    rateType,
                  })}
                  data-testid="button-submit-rate"
                >
                  Add Override
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        {rates.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No rate overrides — using default rate</p>}
        <div className="space-y-2">
          {rates.map(rate => (
            <div key={rate.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`rate-${rate.id}`}>
              <div>
                <p className="text-sm font-medium">{(() => { const tt = String(rate.taskType ?? ""); return tt ? tt.charAt(0).toUpperCase() + tt.slice(1) : "General"; })()}</p>
                <p className="text-xs text-gray-400">{rate.rateType === "hourly" ? "Hourly" : "Fixed"}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium">${(rate.amountCents / 100).toFixed(2)}</span>
                <Button disabled={readOnly} variant="ghost" size="sm" onClick={() => deleteRateMutation.mutate(rate.id)} data-testid={`button-delete-rate-${rate.id}`}>
                  <X className="h-4 w-4 text-red-400" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </TabsContent>

      <TabsContent value="team" className="mt-4">
        <VendorTeamReadOnly vendorId={vendorId} />
      </TabsContent>

      <TabsContent value="reviews" className="mt-4">
        {vendor.aggregateRating && (
          <div className="flex items-center gap-4 mb-4 p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} className={`h-5 w-5 ${s <= Math.round(Number(vendor.aggregateRating)) ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
              ))}
            </div>
            <div>
              <span className="font-semibold">{Number(vendor.aggregateRating).toFixed(1)}</span>
              <span className="text-sm text-gray-500 ml-1">({vendor.totalReviewsCount} reviews)</span>
            </div>
            {vendor.aggregateQuality && (
              <div className="flex gap-3 ml-auto text-xs text-gray-500">
                <span>Quality: {Number(vendor.aggregateQuality).toFixed(1)}</span>
                <span>Comm: {Number(vendor.aggregateCommunication).toFixed(1)}</span>
                <span>Time: {Number(vendor.aggregateTimeliness).toFixed(1)}</span>
              </div>
            )}
          </div>
        )}
        {reviews.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No reviews yet — leave a review from a completed task</p>}
        <div className="space-y-3">
          {reviews.map(review => (
            <Card key={review.id} data-testid={`review-${review.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map(s => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s <= review.overallRating ? "fill-yellow-400 text-yellow-400" : "text-gray-200"}`} />
                    ))}
                  </div>
                  <span className="text-xs text-gray-400">
                    {review.submittedAt ? new Date(review.submittedAt).toLocaleDateString() : ""}
                  </span>
                </div>
                {review.publicComment && <p className="text-sm text-gray-700">{review.publicComment}</p>}
                {review.privateNote && (
                  <p className="text-xs text-gray-400 mt-1 italic">Private: {review.privateNote}</p>
                )}
                <div className="flex gap-3 mt-2 text-xs text-gray-500">
                  {review.qualityRating && <span>Quality: {review.qualityRating}/5</span>}
                  {review.communicationRating && <span>Comm: {review.communicationRating}/5</span>}
                  {review.timelinessRating && <span>Time: {review.timelinessRating}/5</span>}
                  {review.professionalismRating && <span>Prof: {review.professionalismRating}/5</span>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </TabsContent>
    </Tabs>
  );
}
